/**
 * VoyageIdentityStrip — CCD-DASH-014 Phase A · R1.
 *
 * Anchors the whole workspace — ship name, dates, countdown, embark
 * port. Same on every visit; the traveler recognizes it instantly.
 *
 * Design: /app/docs/design/CCD-DASH-014-dashboard-design-sprint.md §1.1 · R1
 */
import React from "react";
import { useCruise } from "@/context/CruiseContext";
import { Ship, MapPin, ArrowRight } from "lucide-react";

const NAVY = "#071e3e";
const GOLD = "#c28e42";

function _dateRange(fromIso, toIso) {
  if (!fromIso || !toIso) return null;
  try {
    const a = new Date(`${fromIso}T00:00:00Z`);
    const b = new Date(`${toIso}T00:00:00Z`);
    const opts = { day: "numeric", month: "short", timeZone: "UTC" };
    const yr = b.getUTCFullYear();
    return `${a.toLocaleDateString("en-US", opts)} – ${b.toLocaleDateString("en-US", opts)}, ${yr}`;
  } catch { return null; }
}

function _daysToEmbark(fromIso) {
  if (!fromIso) return null;
  try {
    const then = new Date(`${fromIso}T00:00:00Z`).getTime();
    const now = new Date();
    const utcMidnightNow = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    const days = Math.round((then - utcMidnightNow) / 86400000);
    return days;
  } catch { return null; }
}

export function VoyageIdentityStrip({ voyage }) {
  const { setRoute } = useCruise();
  const v = voyage || {};
  const shipName = v.ship_name || v.name || "Your cruise";
  const dateRange = _dateRange(v.embark_date || v.embark_at, v.disembark_date || v.disembark_at);
  const embarkPort = v.embark_port?.name || v.embark_port_name;
  const daysToEmbark = _daysToEmbark(v.embark_date || v.embark_at);

  const countdownLine = (() => {
    if (daysToEmbark === null) return null;
    if (daysToEmbark > 0) return `${daysToEmbark} day${daysToEmbark === 1 ? "" : "s"} to embark`;
    if (daysToEmbark === 0) return "Embarkation today";
    return null;
  })();

  return (
    <section
      className="relative overflow-hidden rounded-2xl text-white"
      style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #0F2244 60%, #1C3A6A 100%)` }}
      data-testid="voyage-identity-strip"
    >
      <div className="p-6 flex flex-col lg:flex-row lg:items-center gap-4">
        <div className="flex-1 min-w-0">
          <div
            className="flex items-center gap-1.5 font-body text-[10px] font-semibold uppercase tracking-[0.16em]"
            style={{ color: GOLD }}
          >
            <Ship className="w-3 h-3" />
            <span>Your Voyage</span>
          </div>
          <h1
            className="mt-1 font-display text-3xl sm:text-4xl font-bold tracking-tight leading-none truncate"
            data-testid="voyage-identity-name"
          >
            {shipName}
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 font-body text-[13px] text-white/80">
            {dateRange && <span data-testid="voyage-identity-dates">{dateRange}</span>}
            {embarkPort && (
              <span className="inline-flex items-center gap-1" data-testid="voyage-identity-embark-port">
                <MapPin className="w-3 h-3" />
                {embarkPort}
              </span>
            )}
          </div>
        </div>
        {countdownLine && (
          <div
            className="lg:text-right"
            data-testid="voyage-identity-countdown"
          >
            <div
              className="font-display text-2xl font-semibold tabular-nums"
              style={{ color: GOLD }}
            >
              {countdownLine}
            </div>
          </div>
        )}
        <div className="flex gap-2 lg:ml-4">
          <button
            type="button"
            onClick={() => setRoute("journey")}
            className="inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 px-4 py-2 font-body text-[12px] font-semibold border border-white/20 transition-colors"
            data-testid="voyage-identity-journey-cta"
          >
            Journey <ArrowRight className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setRoute("binder")}
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 font-body text-[12px] font-semibold text-slate-900 transition-colors"
            style={{ background: GOLD }}
            data-testid="voyage-identity-binder-cta"
          >
            Binder <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </section>
  );
}

export default VoyageIdentityStrip;
